import { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { format, addDays, isSameDay } from 'date-fns';

export default function WeekNav({ selectedDay, onDaySelect, onOpenCalendar }) {
  const today = new Date();
  const isToday = isSameDay(selectedDay, today);
  const prevDay = () => onDaySelect(addDays(selectedDay, -1));
  const nextDay = () => onDaySelect(addDays(selectedDay, 1));
  const goToday = () => onDaySelect(today);

  // Touch swipe detection
  const touchStartX = useRef(null);
  const [swiping, setSwiping] = useState(false);
  const [swipeDelta, setSwipeDelta] = useState(0);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    setSwiping(true);
    setSwipeDelta(0);
  };

  const onTouchMove = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.touches[0].clientX - touchStartX.current;
    setSwipeDelta(delta);
  };

  const onTouchEnd = () => {
    if (touchStartX.current !== null) {
      const delta = swipeDelta;
      if (Math.abs(delta) > 40) {
        if (delta < 0) nextDay();
        else prevDay();
      }
    }
    touchStartX.current = null;
    setSwiping(false);
    setSwipeDelta(0);
  };

  return (
    <div
      className="select-none"
      data-testid="week-nav"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className="flex items-center justify-between gap-3">
        {/* Prev arrow */}
        <button
          onClick={prevDay}
          data-testid="week-prev"
          className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        {/* Center: day name + date */}
        <div
          className="flex-1 text-center"
          style={{
            transform: swiping ? `translateX(${Math.max(-40, Math.min(40, swipeDelta * 0.2))}px)` : 'translateX(0)',
            transition: swiping ? 'none' : 'transform 0.2s ease',
          }}
        >
          <div className="flex flex-col items-center leading-none gap-0.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
              {format(selectedDay, 'EEEE')}
            </span>
            <span className="text-3xl font-black font-['Outfit'] tracking-tight">
              {format(selectedDay, 'd')}
            </span>
            <span className="text-[11px] text-muted-foreground font-medium">
              {format(selectedDay, 'MMMM yyyy')}
            </span>
          </div>
        </div>

        {/* Next arrow */}
        <button
          onClick={nextDay}
          data-testid="week-next"
          className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Today pill + calendar icon — only show when not today */}
      <div className="flex items-center justify-center gap-2 mt-2">
        {!isToday && (
          <button
            onClick={goToday}
            data-testid="week-today"
            className="text-[10px] font-bold uppercase tracking-wider text-primary border border-primary/30 bg-primary/5 px-2.5 py-0.5 rounded-full hover:bg-primary/10 transition-colors"
          >
            Today
          </button>
        )}
      </div>
    </div>
  );
}
